import { useState } from 'react';
import { ArrowLeftRight, Check, X } from 'lucide-react';
import { Button, EmptyState, ErrorState, Skeleton, StatusBadge, SubBar } from '@/design/components';
import { useMe } from '@/auth/useMe';
import { usePerm } from '@/access/usePerm';
import { formatDate, weekdayName } from '@/lib/dates';
import { errorMessage } from '@/lib/errors';
import { useSwapMutations, useSwaps, swapStatusLabel, type SwapRequest } from './api';

/**
 * מסך ההחלפות של העובד (PRD 5.4.7).
 *
 * שלושה אזורים, בסדר שבו אדם חושב עליהם: מה מחכה לי לעשות, מה
 * הצעתי, ומה כבר נסגר.
 */
export function SwapsPage() {
  const { me } = useMe();
  const canManage = usePerm('schedule.manage');
  const swaps = useSwaps();
  const { accept, cancel, decide } = useSwapMutations();
  const [error, setError] = useState<string | null>(null);

  const run = (action: () => Promise<unknown>) => {
    setError(null);
    void action().catch((caught) => setError(errorMessage(caught)));
  };

  if (swaps.isPending) return <Skeleton className="m-4 h-40" />;
  if (swaps.isError) return <ErrorState onRetry={() => void swaps.refetch()} />;

  const all = swaps.data ?? [];
  const mine = me?.employeeId;

  // מה שמחכה לי: הצעות פתוחות שאני יכול לקחת, או שממתינות לאישור שלי
  const toTake = all.filter((s) => s.status === 'open' && s.requester_id !== mine);
  const toApprove = canManage ? all.filter((s) => s.status === 'accepted') : [];
  const myOffers = all.filter((s) => s.requester_id === mine && s.status !== 'approved');
  const done = all.filter(
    (s) => s.status === 'approved' || (s.requester_id !== mine && s.status === 'rejected'),
  );

  return (
    <div className="grid content-start gap-4">
      <SubBar title="החלפות משמרת" subtitle="להציע משמרת, לקחת משמרת, ולאשר" />

      <div className="grid gap-6 px-4 pb-8">
        {error && (
          <p role="alert" className="m-0 text-body-sm text-bad">
            {error}
          </p>
        )}

        {all.length === 0 && (
          <EmptyState
            title="אין בקשות החלפה"
            description="אפשר להציע משמרת להחלפה ממסך הסידור שלי."
          />
        )}

        {toApprove.length > 0 && (
          <Section title="ממתין לאישור שלך" count={toApprove.length}>
            {toApprove.map((swap) => (
              <SwapCard key={swap.id} swap={swap}>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="primary"
                    onClick={() =>
                      run(() => decide.mutateAsync({ requestId: swap.id, approve: true }))
                    }
                  >
                    <Check size={20} strokeWidth={1.75} aria-hidden="true" />
                    אישור ההחלפה
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() =>
                      run(() => decide.mutateAsync({ requestId: swap.id, approve: false }))
                    }
                  >
                    <X size={20} strokeWidth={1.75} aria-hidden="true" />
                    דחייה
                  </Button>
                </div>
              </SwapCard>
            ))}
          </Section>
        )}

        {toTake.length > 0 && (
          <Section title="משמרות שמוצעות" count={toTake.length}>
            {toTake.map((swap) => (
              <SwapCard key={swap.id} swap={swap}>
                <Button variant="primary" onClick={() => run(() => accept.mutateAsync(swap.id))}>
                  <ArrowLeftRight size={20} strokeWidth={1.75} aria-hidden="true" />
                  אני לוקח/ת את המשמרת
                </Button>
              </SwapCard>
            ))}
          </Section>
        )}

        {myOffers.length > 0 && (
          <Section title="ההצעות שלי" count={myOffers.length}>
            {myOffers.map((swap) => (
              <SwapCard key={swap.id} swap={swap}>
                {(swap.status === 'open' || swap.status === 'accepted') && (
                  <Button
                    variant="secondary"
                    onClick={() => run(() => cancel.mutateAsync(swap.id))}
                  >
                    ביטול ההצעה
                  </Button>
                )}
              </SwapCard>
            ))}
          </Section>
        )}

        {done.length > 0 && (
          <Section title="הסתיימו" count={done.length}>
            {done.map((swap) => (
              <SwapCard key={swap.id} swap={swap} />
            ))}
          </Section>
        )}
      </div>
    </div>
  );
}

function Section({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <section className="grid gap-2">
      <h2 className="m-0 font-display text-display-m font-semibold">
        {title} <span className="text-body-sm font-normal text-muted">({count})</span>
      </h2>
      {children}
    </section>
  );
}

function SwapCard({ swap, children }: { swap: SwapRequest; children?: React.ReactNode }) {
  const shift = swap.shift_assignments;
  const tone =
    swap.status === 'approved'
      ? 'good'
      : swap.status === 'rejected' || swap.status === 'cancelled'
        ? 'bad'
        : 'warn';

  const name = (p: { first_name: string; last_name: string } | null) =>
    p ? `${p.first_name} ${p.last_name}` : '';

  return (
    <div className="grid gap-2 rounded-md border border-line bg-raised p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-title font-medium">
          {shift ? `${weekdayName(shift.date)} ${formatDate(shift.date)}` : 'משמרת'}
          {shift?.shift_types && (
            <span className="ms-2 text-body-sm font-normal text-muted">
              {shift.shift_types.name}{' '}
              <span className="tabular" dir="ltr">
                {shift.shift_types.start_time.slice(0, 5)}–{shift.shift_types.end_time.slice(0, 5)}
              </span>
            </span>
          )}
        </span>
        <StatusBadge tone={tone}>{swapStatusLabel[swap.status]}</StatusBadge>
      </div>

      <p className="m-0 text-body-sm text-muted">
        {name(swap.requester)} מציע/ה
        {swap.target && ` ל${name(swap.target)}`}
        {swap.accepted_by && ` · ${name(swap.accepted_by)} לקח/ה`}
        {shift?.branches && ` · ${shift.branches.name}`}
      </p>

      {swap.note && <p className="m-0 text-body-sm text-ink">"{swap.note}"</p>}

      {children}
    </div>
  );
}
